import { Dialog } from "@kobalte/core/dialog";
import { InvitationsSyncRequestConfig, useStorytellClient } from "@storytell/client";
import type { ProjectId } from "@storytell/types";
import { AnimateChildren } from "@storytell/ui/animation";
import { StButton } from "@storytell/ui/buttons";
import { TbSparkles, TbX } from "solid-icons/tb";
import { type Component, createResource, createSignal } from "solid-js";
import { useGateActionByAuthenticated } from "~/hooks/useGateActionByAuthenticated";
import { useWire } from "~/wire";
import { ProjectShareSection } from "./ProjectShareSection";
import { ProjectSwitcherModal } from "./ProjectSwitcherModal";
import { mapRoleToUI } from "./roleUtils";

interface InviteShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: ProjectId;
}

export const InviteShareModal: Component<InviteShareModalProps> = (props) => {
  const wire = useWire();
  const client = useStorytellClient();
  const [isProjectModalOpen, setIsProjectModalOpen] = createSignal(false);

  const openProjectModal = useGateActionByAuthenticated(
    () => ({
      title: "Sign up to create Projects",
      description: "Projects are a great way to collaborate with your colleagues, team and community.",
    }),
    () => setIsProjectModalOpen(true),
  );

  // Fetch invitations for this project
  const [allInvitations, { refetch: refetchInvitations, mutate: mutateInvitations }] = createResource(
    () => props.isOpen && wire.auth.isReady() && wire.auth.userId() && props.projectId,
    async () => {
      const request = InvitationsSyncRequestConfig.withBearerToken(wire.auth.token());
      const response = await client.fetch(request, undefined, undefined);

      if (response.isErr()) {
        console.error("Failed to fetch invitations", response.error);
        return [];
      }

      return Object.values(response.value.invites);
    },
    {
      deferStream: true,
      initialValue: [],
    },
  );

  const invitations = () => {
    const all = allInvitations() || [];
    return all.filter((invite) => invite.projectId === props.projectId);
  };

  // Get current user's role to customize description text
  const getUserRole = () => {
    const project = wire.projects.get(props.projectId);
    if (!project?.role) return null;
    return mapRoleToUI(project.role);
  };

  const getDescriptionSuffix = () => {
    const role = getUserRole();

    if (role === "Collaborator") {
      return "will be able to collaborate with you across all chats and knowledge in this project.";
    } else if (role === "Reader") {
      return "will be able to read all chats and knowledge in this project.";
    } else {
      // Owner or default
      return "will have access to all chats and knowledge in this project.";
    }
  };

  return (
    <>
      <AnimateChildren
        show={props.isOpen}
        enter={{ opacity: [0, 1], y: [10, 0] }}
        exit={{ opacity: [1, 0], y: [0, 10] }}
      >
        {(anim) => (
          <Dialog
            open={anim.isMounted()}
            onOpenChange={(val) => {
              if (!val) props.onClose();
            }}
          >
            <Dialog.Portal>
              <Dialog.Overlay class="fixed z-90 inset-0 bg-black/50 dark:bg-white/10" />
              <div ref={anim.ref} class="fixed z-90 inset-0 flex items-center justify-center p-4">
                <Dialog.Content class="max-h-[85vh] relative w-full text-primary-text max-w-3xl bg-panel overflow-y-auto rounded-xl shadow-2xl">
                  <div class="sticky top-0 bg-panel border-b border-primary-border px-6 py-4 flex items-start justify-between">
                    <div class="flex-1">
                      <div class="flex items-center gap-3 mb-3">
                        <div class="p-2 rounded-lg bg-gradient-to-br from-brand/20 to-brand/10">
                          <TbSparkles class="w-5 h-5 text-brand" />
                        </div>
                        <Dialog.Title class="text-xl font-semibold bg-gradient-to-r from-brand to-brand/80 bg-clip-text text-transparent">
                          Invite Your Colleagues
                        </Dialog.Title>
                      </div>
                      <Dialog.Description class="text-sm text-muted-text">
                        <div>
                          Anyone you invite to{" "}
                          <span class="font-medium text-primary-text">{wire.projects.get(props.projectId)?.name}</span>{" "}
                          {getDescriptionSuffix()}
                        </div>
                        <div class="mt-2">
                          You can also create a{" "}
                          <button
                            type="button"
                            onClick={openProjectModal}
                            class="text-brand hover:underline font-medium"
                          >
                            new project
                          </button>{" "}
                          to share instead of sharing this one.
                        </div>
                      </Dialog.Description>
                    </div>
                    <Dialog.CloseButton as={StButton} class="flex-shrink-0 ml-4">
                      <TbX class="w-5 h-5" />
                    </Dialog.CloseButton>
                  </div>

                  <div class="p-6">
                    <ProjectShareSection
                      embedded
                      projectId={props.projectId}
                      invitations={invitations}
                      refetchInvitations={refetchInvitations}
                      mutateInvitations={mutateInvitations}
                    />
                  </div>
                </Dialog.Content>
              </div>
            </Dialog.Portal>
          </Dialog>
        )}
      </AnimateChildren>

      {/* Project Switcher Modal */}
      <ProjectSwitcherModal open={isProjectModalOpen()} onOpenChange={setIsProjectModalOpen} initialView="form" />
    </>
  );
};
